import React, { useRef, useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";

import axios, { getErrorMessage } from "../../api/axios";
// ADDED: the login screen now feeds the shared auth context
import { useAuth } from "../Components/AuthProvider";

export default function Login () { 

    // Consts and vars
    const navigate = useNavigate();
    const location = useLocation();
    const { login, isAuthenticated } = useAuth();

    const [action, setAction] = useState('Login');

    const usernameInput = useRef(null);

    const emailInput = useRef(null);
    const [validEmail, setValidEmail] = useState(false);
    const [emailFocus, setEmailFocus] = useState(false);

    const [passwordInput, setPassword] = useState("");
    const [validPassword, setValidPassword] = useState(false);
    const [passwordFocus, setPasswordFocus] = useState(false);

    const [confirmPasswordInput, setConfirmPassword] = useState("");
    const [matchedPassword, setMatchedPassword] = useState(false);
    const [confirmPasswordFocus, setConfirmPasswordFocus] = useState(false);

    const [errorMessage, setErrorMessage] = useState("");
    // ADDED: submit state, so the button cannot be double-clicked into two accounts
    const [isSubmitting, setIsSubmitting] = useState(false);

    // ADDED: state for the "forgot password" panel
    const [showForgotPassword, setShowForgotPassword] = useState(false);
    const [forgotEmail, setForgotEmail] = useState("");
    const [forgotMessage, setForgotMessage] = useState("");

    const errRef = useRef();

    const EMAIL_REGEX = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    const PASSWORD_REGEX = /^(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/;

    // Where to land after signing in: back where they were headed, or the models page
    // Includes the query string, so "/models?search=camaro" survives the login round trip
    const redirectTo = location.state?.from
        ? `${location.state.from.pathname}${location.state.from.search || ""}`
        : "/models";

    // MODIFIED: redirect happens in an effect. It used to call navigate() during render,
    // which React does not allow and which pointed at "/Homescreen/index", not a real route.
    useEffect(() => {
        if (isAuthenticated) {
            navigate(redirectTo, { replace: true });
        }
    }, [isAuthenticated, navigate, redirectTo]);

    // Clean values after switching between login and register
    const cleanValues = () => {
        if (usernameInput.current) usernameInput.current.value = "";
        if (emailInput.current) emailInput.current.value = "";
        setPassword("");
        setConfirmPassword("");
    }

    // Focus on first input field
    useEffect(() => {
        cleanValues();
        // MODIFIED: guarded with ?. - the refs are null on the first paint of each mode
        action === "Login" ? emailInput.current?.focus() : usernameInput.current?.focus();
    }, [action]);

    // Validate input fields
    const handleEmailChange = () => {
        if (action === "Sign up") {
            setValidEmail(EMAIL_REGEX.test(emailInput.current.value));
        }
    };

    const handlePasswordChange = (e) => {
        setPassword(e.target.value);
        if (action === "Sign up") {
            setValidPassword(PASSWORD_REGEX.test(e.target.value));
        }
        // MODIFIED: re-check the match when the first password changes, not only the second
        setMatchedPassword(confirmPasswordInput === e.target.value);
    };

    const handleConfirmPasswordChange = (e) => {
        setConfirmPassword(e.target.value);
        setMatchedPassword(e.target.value === passwordInput);
    };

    // MODIFIED: clear the error whenever the user switches mode
    useEffect(() => {
        setErrorMessage("");
    }, [action]);

    // Register function
    const registerNewUser = async (e) =>{

        e.preventDefault();
            
        const username = usernameInput.current?.value || "";
        const email = emailInput.current?.value || "";
        const password = passwordInput;

        if (!username || !email || !password) {
            setErrorMessage("Error: All fields are required.");
            return;
        }

        // ADDED: these checks existed as hints under the fields but never blocked submission
        if (!EMAIL_REGEX.test(email)) {
            setErrorMessage("Error: Please enter a valid email.");
            return;
        }
        if (!PASSWORD_REGEX.test(password)) {
            setErrorMessage("Error: Password must have 8+ characters, an uppercase letter, a number and a special character.");
            return;
        }
        if (password !== confirmPasswordInput) {
            setErrorMessage("Error: Passwords do not match.");
            return;
        }

        // Save new user to database
        const newUser = {
            name: username,
            email: email,
            password: password
        };

        setIsSubmitting(true);
        try {
            const response = await axios.post('/register', newUser,{
                withCredentials: true,
                headers: {
                    'Content-Type': 'application/json',
                }
            });

            // MODIFIED: store the session instead of only logging it. The effect above
            // handles the redirect once isAuthenticated flips.
            login(response.data.token, response.data.user);
        } catch (error) {
            // MODIFIED: axios puts the status on error.response.status, not error.status,
            // so none of these branches used to match.
            if (error.response?.status === 409) {
                setErrorMessage("Error: User already exists.");
            } else {
                setErrorMessage(getErrorMessage(error));
            }
            console.log(error);
        } finally {
            setIsSubmitting(false);
        }
    }
    // Login function
    const loginUser = async (e) =>{
        e.preventDefault();

        const email = emailInput.current?.value || "";
        const password = passwordInput;

        if (!email || !password) {
            setErrorMessage("Error: All fields are required.");
            return;
        }

        const newUser = {
            email: email,
            password: password
        };

        setIsSubmitting(true);
        try {
            const response = await axios.post('/login', newUser, {
                withCredentials: true,
                headers: {
                    'Content-Type': 'application/json'
                }
            });
            // MODIFIED: same as register - hand the token to the auth context
            login(response.data.token, response.data.user);
        } catch (error) {
            if (error.response?.status === 401) {
                setErrorMessage("Error: Invalid email or password.");
            } else {
                setErrorMessage(getErrorMessage(error));
            }
            console.log(error);
        } finally {
            setIsSubmitting(false);
        }
    }

    // ADDED: asks the server to email a reset link. The server always answers 200,
    // so this never reveals whether an address is registered.
    const requestPasswordReset = async (e) => {
        e.preventDefault();
        if (!forgotEmail) {
            setForgotMessage("Please enter your email.");
            return;
        }

        setIsSubmitting(true);
        try {
            const response = await axios.post('/forgot-password', { email: forgotEmail });
            setForgotMessage(response.data.message);
        } catch (error) {
            setForgotMessage(getErrorMessage(error));
        } finally {
            setIsSubmitting(false);
        }
    }

    // MODIFIED: the whole layout is now fluid. It was a fixed w-1/2 with p-20 padding,
    // which left almost no usable width on a phone.
    // MODIFIED (styling): the solid red backdrop is now a dark, theme-aware panel with the
    // SpeedVault wordmark above the card, so the login screen belongs to the same app as
    // the rest of the pages instead of being a red block on its own.
    return (
        <section className="min-h-screen flex items-center justify-center p-4 relative overflow-hidden"
                 style={{ backgroundColor: 'var(--surface)' }}>

            {/* Decorative red glow, same device as the landing hero */}
            <div aria-hidden="true"
                 className="pointer-events-none absolute -top-32 left-1/2 -translate-x-1/2 h-[26rem] w-[26rem] rounded-full blur-3xl opacity-25"
                 style={{ background: 'radial-gradient(circle, var(--accent) 0%, transparent 70%)' }} />

            <div className="relative w-full max-w-md mx-auto animate-fade-up">

                {/* ADDED: the wordmark, so the login screen is recognisably SpeedVault */}
                <button type="button" onClick={() => navigate('/')}
                    className="block mx-auto mb-6 font-display font-bold text-3xl tracking-tight">
                    <span className="cv-strong">SPEED</span><span className="cv-accent italic">VAULT</span>{/* MODIFIED: renamed from CLOUD VAULT */}
                </button>

                <div className="cv-card overflow-hidden">
                    <div className="cv-stripe" />
                    <div className="flex flex-col gap-5 px-6 py-8 sm:px-9 sm:py-9">

                        {errorMessage &&
                            <p ref={errRef} role="alert" className="cv-alert-error text-center">
                                {errorMessage}
                            </p>
                        }

                        {/* MODIFIED: the heading follows the reset panel, so the screen does not
                            still say "Login" while asking for a reset email. */}
                        <div className="text-center space-y-1">
                            <p className="cv-eyebrow">
                                {showForgotPassword ? 'Forgot password' : (action === 'Login' ? 'Welcome back' : 'Join the club')}
                            </p>
                            <h1 className="cv-display text-3xl sm:text-4xl">
                                {showForgotPassword ? 'Reset password' : action}
                            </h1>
                        </div>

                        <div className="relative flex items-center w-full">
                            <div className="flex-grow border-t" style={{ borderColor: 'var(--border-subtle)' }}></div>
                            <span className="mx-3">
                                <img src="img/racing-flag.png" alt="" className="h-6 w-6 dark:invert" />
                            </span>
                            <div className="flex-grow border-t" style={{ borderColor: 'var(--border-subtle)' }}></div>
                        </div>

                        {/* MODIFIED: the email/password form used to stay on screen underneath the
                            reset panel, so the page showed two competing forms at once. It is now
                            swapped out entirely while the reset panel is open. */}
                        {!showForgotPassword &&
                            /* a real onSubmit, so Enter submits the form */
                            <form className="flex flex-col space-y-5"
                                  onSubmit={action === "Sign up" ? registerNewUser : loginUser}>
                                {action === "Sign up" &&
                                <>
                                    <div className="flex flex-row space-x-3 items-center">
                                        <img src="img/user.png" alt="" className="h-5 w-5 shrink-0 opacity-70 dark:invert" />
                                        <input
                                            type="text"
                                            placeholder="Enter name"
                                            id="name"
                                            name="name"
                                            ref={usernameInput}
                                            required
                                            autoComplete="off"
                                            className="cv-input-line"
                                        />
                                    </div>
                                </>
                                }

                                <div className="flex flex-row space-x-3 items-center">
                                    <img src="img/mail.png" alt="" className="h-5 w-5 shrink-0 opacity-70 dark:invert" />
                                    <input
                                        type="email"
                                        placeholder="Enter email"
                                        id="email"
                                        name="email"
                                        ref={emailInput}
                                        required
                                        autoComplete="off"
                                        onFocus={() => setEmailFocus(true)}
                                        onBlur={() => setEmailFocus(false)}
                                        onChange={handleEmailChange}
                                        className="cv-input-line"
                                    />
                                </div>
                                {action === "Sign up" && emailFocus && !validEmail &&
                                        <p className="text-xs cv-accent">Enter a valid email.</p>
                                }

                                <div className="flex flex-row space-x-3 items-center">
                                    <img src="img/lock-white.png" alt="" className="h-5 w-5 shrink-0 opacity-70 dark:invert" />
                                    <input
                                        type="password"
                                        placeholder="Enter password"
                                        id="password"
                                        name="password"
                                        value={passwordInput}
                                        required
                                        autoComplete={action === "Sign up" ? "new-password" : "current-password"}
                                        onFocus={() => setPasswordFocus(true)}
                                        onBlur={() => setPasswordFocus(false)}
                                        onChange={handlePasswordChange}
                                        className="cv-input-line"
                                    />
                                </div>
                                {action === "Sign up" && passwordFocus && !validPassword &&
                                    <p className="text-xs cv-accent">Password must contain at least 8 characters,
                                                           one uppercase letter, one number, and one special character.</p>
                                }
                                {action === "Sign up" &&
                                <>
                                    <div className="flex flex-row space-x-3 items-center">
                                        <img src="img/lock-black.png" alt="" className="h-5 w-5 shrink-0 opacity-70 dark:invert" />
                                        <input
                                            type="password"
                                            placeholder="Confirm password"
                                            id="confirmPassword"
                                            name="confirmPassword"
                                            value={confirmPasswordInput}
                                            required
                                            autoComplete="new-password"
                                            onFocus={() => setConfirmPasswordFocus(true)}
                                            onBlur={() => setConfirmPasswordFocus(false)}
                                            onChange={handleConfirmPasswordChange}
                                            className="cv-input-line"
                                        />
                                    </div>
                                    { confirmPasswordFocus && !matchedPassword &&
                                            <p className="text-xs cv-accent">Passwords do not match.</p>
                                    }
                                </>
                                }

                                {/* MODIFIED: moved inside the form so the submit button is the form's submit */}
                                <button type="submit"
                                    disabled={isSubmitting}
                                    className="cv-btn cv-btn-primary w-full py-3 uppercase">
                                    {isSubmitting ? "Please wait..." : action}
                                </button>
                            </form>
                        }

                        {action === "Login" && !showForgotPassword &&
                            <div className="text-right text-xs cv-muted">
                                {/* MODIFIED: this was a dead <a href="">. It now opens the reset panel. */}
                                <p>Forgot password? <button type="button" className="cv-accent underline"
                                    onClick={() => {
                                        // MODIFIED: also clear any login error, otherwise a failed
                                        // sign-in attempt stays on screen over the reset panel
                                        setShowForgotPassword(true);
                                        setForgotMessage("");
                                        setErrorMessage("");
                                    }}>Click here</button>.</p>
                            </div>
                        }

                        {/* MODIFIED: this panel replaces the login form rather than sitting under it,
                            so the border-top that separated the two is gone. */}
                        {showForgotPassword &&
                            <form onSubmit={requestPasswordReset} className="space-y-4 animate-fade-up">
                                <p className="text-sm cv-body">Enter your email and we will send you a reset link.</p>
                                <input
                                    type="email"
                                    required
                                    autoFocus
                                    autoComplete="email"
                                    value={forgotEmail}
                                    onChange={(e) => setForgotEmail(e.target.value)}
                                    placeholder="Enter email"
                                    className="cv-input"
                                />
                                {forgotMessage && <p className="text-xs cv-body p-2 rounded" style={{ backgroundColor: 'var(--surface-sunken)' }}>{forgotMessage}</p>}
                                <div className="flex gap-2">
                                    <button type="submit" disabled={isSubmitting} className="cv-btn cv-btn-primary flex-1">
                                        Send link
                                    </button>
                                    <button type="button"
                                        onClick={() => { setShowForgotPassword(false); setForgotEmail(""); setForgotMessage(""); setErrorMessage(""); }}
                                        className="cv-btn cv-btn-quiet flex-1">
                                        Back to login
                                    </button>
                                </div>
                            </form>
                        }

                        {/* MODIFIED: hidden while resetting - switching to Sign up mid-reset
                            just left the user somewhere they did not ask to be. */}
                        {!showForgotPassword &&
                            <div className="text-center text-xs cv-muted">
                                {action === "Login" ?
                                <p> Not a member? <span className="cv-accent cursor-pointer font-semibold" onClick={() => setAction("Sign up")}>Sign Up</span>.</p>
                                :<p>Already a member? <span className="cv-accent cursor-pointer font-semibold" onClick={() => setAction("Login")}>Login</span>.</p>
                                }
                            </div>
                        }

                        {/* ADDED: let someone look around without an account. The landing page is
                            public; Models and Collections still ask for a sign-in when opened. */}
                        {!showForgotPassword &&
                            <div className="pt-1" style={{ borderTop: '1px solid var(--border-subtle)' }}>
                                <button type="button" onClick={() => navigate('/')}
                                    className="cv-btn cv-btn-quiet w-full mt-4 text-sm">
                                    Continue as a guest
                                </button>
                                <p className="text-center text-xs cv-muted mt-2">
                                    You can browse the site, but saving cars needs an account.
                                </p>
                            </div>
                        }
                    </div>
                </div>
            </div>
        </section>
    );
}
